import { useEffect, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  AccountFundingOption,
  BankingPaymentRail,
  DefaultBankAccountList,
  formattedCurrency,
  FundingSource,
  FundingTrigger,
  sanitizeToNumeric,
  TransactionTypesOptions,
} from 'vopay';
import { InfoBanner } from '@/components/UI';
import {
  getAvailableBalance,
  getFundingSettlementTimeLabel,
} from '@/hooks/useFields/TransactionCreationFields/useFundingOptionField';
import useFetchWorkflows from '@/hooks/useApi/useFetchWorkflows';
import useTransactionApprovalAccess from '@/hooks/useTransactionApprovalAccess';
import { useToast } from '@/providers/Toast.provider';
// Kept in a separate, non-component file: react-refresh/only-export-components (a component file
// should only export components — mixing in a type/function here breaks Fast Refresh).
import { SinglePaymentSelectionMethod } from './singlePaymentSelectionMethod';

// Maps this form's payment method to the backend's approval-workflow TransactionType. Keep in
// sync with SinglePaymentPage's submit logic.
function getPaymentTransactionType(
  paymentSelectionMethod: SinglePaymentSelectionMethod | undefined,
  bankingPaymentRail: BankingPaymentRail | undefined,
): TransactionTypesOptions | undefined {
  switch (paymentSelectionMethod) {
    case 'bank':
      switch (bankingPaymentRail) {
        case 'eft':
          return 'EFT Withdrawal';
        case 'ach':
          return 'ACH Withdrawal';
        case 'vopayinstant':
          return 'VoPayInstant Withdraw';
        case 'fedwire':
          return 'Wire';
        case 'rtp':
          return 'RTP Withdrawal';
        default:
          return undefined;
      }
    case 'credit':
      return 'Credit Card';
    case 'debitcard':
      return 'OCT';
    case 'paypal':
      return 'PayPal Withdrawal';
    case 'venmo':
      return 'Venmo Withdrawal';
    case 'zelle':
      return 'Zelle Withdrawal';
    case 'email':
      return 'Interac Money Request';
    default:
      return undefined;
  }
}

type FundingBannerProps = {
  fundingTrigger: FundingTrigger;
  isScheduled: boolean;
  fundingOptions: AccountFundingOption[];
  payFrom: FundingSource | '';
  amount: string;
  currency: string; // display only (interpolated into the banner copy) — not SupportedCurrencies
  operationalBankAccount?: DefaultBankAccountList;
  paymentSelectionMethod: SinglePaymentSelectionMethod | undefined;
  bankingPaymentRail: BankingPaymentRail | undefined;
};

// Conditional funding banner, per the Payments Confluence spec. Hidden for scheduled payments and
// whenever an active approval workflow matches (the approval flow owns the messaging then).
export default function FundingBanner({
  fundingTrigger,
  isScheduled,
  fundingOptions,
  payFrom,
  amount,
  currency,
  operationalBankAccount,
  paymentSelectionMethod,
  bankingPaymentRail,
}: FundingBannerProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'SinglePayment.Form' });
  const { workflows, fetchWorkflows } = useFetchWorkflows();
  const canUseTransactionApproval = useTransactionApprovalAccess();
  const toast = useToast();

  useEffect(() => {
    if (!canUseTransactionApproval) return;

    (async () => {
      try {
        await fetchWorkflows();
      } catch {
        toast.neutral(t('fetchWorkflowsError', { defaultValue: 'Could not check approval workflows' }));
      }
    })();
  }, [canUseTransactionApproval]);

  const selectedFundingOption = useMemo(
    () => fundingOptions.find((option) => option.Source === payFrom),
    [fundingOptions, payFrom],
  );

  const balanceOption = useMemo(() => fundingOptions.find((option) => option.Source === 'balance'), [fundingOptions]);

  const hasPositiveBalance = getAvailableBalance(balanceOption) > 0;

  // Mirrors the backend's workflow match: active workflow whose [MinAmount, MaxAmount) range covers Amount.
  const requiresApproval = useMemo(() => {
    const transactionType = getPaymentTransactionType(paymentSelectionMethod, bankingPaymentRail);
    if (!transactionType || !amount) return false;

    const group = workflows.find((w) => w.TransactionType === transactionType);
    if (!group) return false;

    const numericAmount = Number(sanitizeToNumeric(amount));
    return group.Workflows.some(
      (workflow) =>
        workflow.IsActive &&
        numericAmount >= Number(workflow.MinAmount) &&
        (workflow.MaxAmount === null || numericAmount < Number(workflow.MaxAmount)),
    );
  }, [workflows, paymentSelectionMethod, bankingPaymentRail, amount]);

  const banner = useMemo(() => {
    if (isScheduled || requiresApproval) return undefined;

    if (fundingTrigger === 'never') {
      if (!selectedFundingOption) return undefined;

      if (selectedFundingOption.Source === 'bank') {
        return { i18nKey: 'FundingBanner.bankAccount', values: {} };
      }

      if (selectedFundingOption.Source === 'credit') {
        const feePercent = Number(selectedFundingOption.FeePercent || 0);
        const feePerTransaction = Number(selectedFundingOption.FeePerTransaction || 0);
        const computedFee =
          feePercent > 0 ? (Number(sanitizeToNumeric(amount) || 0) * feePercent) / 100 : feePerTransaction;
        const feeAmount = `${selectedFundingOption.Currency} ${formattedCurrency(String(computedFee))}`;
        const settlementTime = getFundingSettlementTimeLabel(t, selectedFundingOption.SettlementTime);

        return { i18nKey: 'FundingBanner.creditCard', values: { feeAmount, settlementTime } };
      }

      return undefined;
    }

    if (fundingTrigger === 'insufficient-balance' || fundingTrigger === 'always') {
      if (!amount || !operationalBankAccount) return undefined;

      const isShort = Number(sanitizeToNumeric(amount)) > getAvailableBalance(balanceOption);
      if (fundingTrigger === 'insufficient-balance' && !isShort) return undefined;

      const fundingAmount = `${currency} ${formattedCurrency(amount)}`;
      const bankAccount = `${operationalBankAccount.InstitutionName} ${operationalBankAccount.AccountNumber}`;

      return { i18nKey: 'FundingBanner.autoFunded', values: { amount: fundingAmount, bankAccount } };
    }

    return undefined;
  }, [
    fundingTrigger,
    isScheduled,
    requiresApproval,
    selectedFundingOption,
    balanceOption,
    amount,
    currency,
    operationalBankAccount,
    t,
  ]);

  if (!banner) return null;

  const balanceNoteKey = `${banner.i18nKey}BalanceNote`;

  return (
    <InfoBanner colorScheme="neutral">
      <Trans
        t={t}
        i18nKey={banner.i18nKey}
        values={banner.values}
        defaults={FUNDING_BANNER_DEFAULTS[banner.i18nKey]}
        components={{ bold: <span style={{ fontWeight: 600 }} /> }}
      />
      {hasPositiveBalance && (
        <Trans
          t={t}
          i18nKey={balanceNoteKey}
          defaults={FUNDING_BANNER_DEFAULTS[balanceNoteKey]}
          components={{ bold: <span style={{ fontWeight: 600 }} /> }}
        />
      )}
    </InfoBanner>
  );
}

const FUNDING_BANNER_DEFAULTS: Record<string, string> = {
  'FundingBanner.bankAccount':
    'This payment will stay in <bold>Waiting</bold> until the funding settles, then be processed automatically.',
  'FundingBanner.bankAccountBalanceNote': " Your VoPay balance won't be used — the funding covers the full payment.",
  'FundingBanner.creditCard':
    'This payment will remain <bold>Waiting</bold> until the funding settles, then it will be processed automatically. An additional fee of <bold>{{feeAmount}}</bold> will be charged, and the funding is expected to settle within <bold>{{settlementTime}}</bold>.',
  'FundingBanner.creditCardBalanceNote':
    ' Your VoPay balance will not be used, as the funding covers the full payment amount.',
  'FundingBanner.autoFunded':
    'A funding transaction for <bold>{{amount}}</bold> will first be created to withdraw funds from <bold>{{bankAccount}}</bold>. This payment will remain <bold>Waiting</bold> until the funds settle, then it will be processed automatically.',
  'FundingBanner.autoFundedBalanceNote': ' Your VoPay balance will not be used.',
};
